"use client";

import { StateCreator } from "zustand";

// TKA trend series labels (default visible set)
export const TKA_TREND_SERIES = [
  "Active Robotic Time",
  "Completion of Femoral Cuts",
  "Completion of Tibial Cuts",
  "Final Alignment",
  "Overall Surgery",
  "Registration & Planning",
] as const;

// UKA trend series labels
export const UKA_TREND_SERIES = [
  "Overall Surgery",
  "Active Robotic Time",
  "Landmark Acquisition Time",
  "Planning",
  "Bone Cuts",
  "Final Alignment",
] as const;

export type WorkflowProcedureType = "TKA" | "UKA";

export type WorkflowAnalyticsState = {
  workflowProcedureType: WorkflowProcedureType;
  workflowDateFrom: string;
  workflowDateTo: string;
  workflowClassificationFilter: string[];
  workflowLocationFilter: string[];
  workflowVisibleTrendSeries: string[];
  // Derived option lists (populated when data loads)
  workflowClassificationOptions: string[];
  workflowLocationOptions: string[];

  setWorkflowProcedureType: (type: WorkflowProcedureType) => void;
  setWorkflowDateFrom: (date: string) => void;
  setWorkflowDateTo: (date: string) => void;
  setWorkflowClassificationFilter: (value: string[]) => void;
  setWorkflowLocationFilter: (value: string[]) => void;
  setWorkflowVisibleTrendSeries: (series: string[]) => void;
  toggleWorkflowTrendSeries: (series: string) => void;
  setWorkflowClassificationOptions: (options: string[]) => void;
  setWorkflowLocationOptions: (options: string[]) => void;
  resetWorkflowFilters: () => void;
};

export const createWorkflowAnalyticsSlice: StateCreator<WorkflowAnalyticsState> =
  (set) => ({
    workflowProcedureType: "TKA",
    workflowDateFrom: "",
    workflowDateTo: "",
    workflowClassificationFilter: [],
    workflowLocationFilter: [],
    workflowVisibleTrendSeries: [...TKA_TREND_SERIES],
    workflowClassificationOptions: [],
    workflowLocationOptions: [],

    setWorkflowProcedureType: (type) =>
      set({
        workflowProcedureType: type,
        workflowClassificationFilter: [],
        workflowLocationFilter: [],
        workflowDateFrom: "",
        workflowDateTo: "",
        workflowVisibleTrendSeries:
          type === "TKA" ? [...TKA_TREND_SERIES] : [...UKA_TREND_SERIES],
      }),

    setWorkflowDateFrom: (date) => set({ workflowDateFrom: date }),

    setWorkflowDateTo: (date) => set({ workflowDateTo: date }),

    setWorkflowClassificationFilter: (value) =>
      set({ workflowClassificationFilter: value }),

    setWorkflowLocationFilter: (value) =>
      set({ workflowLocationFilter: value }),

    setWorkflowVisibleTrendSeries: (series) =>
      set({ workflowVisibleTrendSeries: series }),

    setWorkflowClassificationOptions: (options) =>
      set({ workflowClassificationOptions: options }),

    setWorkflowLocationOptions: (options) =>
      set({ workflowLocationOptions: options }),

    toggleWorkflowTrendSeries: (series) =>
      set((state) => {
        const current = state.workflowVisibleTrendSeries;
        const next = current.includes(series)
          ? current.filter((s) => s !== series)
          : [...current, series];
        return { workflowVisibleTrendSeries: next };
      }),

    resetWorkflowFilters: () =>
      set((state) => ({
        workflowDateFrom: "",
        workflowDateTo: "",
        workflowClassificationFilter: [...state.workflowClassificationOptions],
        workflowLocationFilter: [...state.workflowLocationOptions],
        workflowVisibleTrendSeries:
          state.workflowProcedureType === "TKA"
            ? [...TKA_TREND_SERIES]
            : [...UKA_TREND_SERIES],
      })),
  });
